import { FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError } from '@/lib/apiClient'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { SupportLine, TermsConsent } from '@/components/ui/TermsConsent'
import { termsVersion } from '@/lib/legal'
import { ErrorState, LoadingState, Notice } from '@/components/ui/Feedback'
import { NativeSelect } from '@/components/ui/NativeSelect'
import { PasswordField } from '@/components/ui/PasswordField'
import { formatDate } from '@/lib/format'
import { lineOfBusinessHint, lineOfBusinessLabels, lineOfBusinessOrder, type LineOfBusiness } from '@/lib/modules'

type Origin = 'KNOWN_EXISTING' | 'UNVERIFIED_EXISTING' | 'NEW_PURCHASE'
type Company = { id: string; businessName: string; environmentId: string; subdomain?: string; provisioningStatus: 'AWAITING_PAYMENT' | 'PREPARING' | 'READY' | 'FAILED' | 'SUSPENDED' | 'DELETED'; webUrl?: string; origin: Origin; role: string; lineOfBusiness?: LineOfBusiness | null; trialAvailable?: boolean }
const deliveryLabels: Record<Company['provisioningStatus'], string> = { READY: 'Entorno listo', PREPARING: 'Preparando el entorno', FAILED: 'La preparación requiere atención', AWAITING_PAYMENT: 'Pendiente de pago o prueba', SUSPENDED: 'Servicio suspendido: contrata o renueva tu plan en Licencias; sus datos se borran a los 90 días', DELETED: 'Datos borrados tras 90 días suspendido: contrata un plan en Licencias para empezar de nuevo' }
/**
 * The environment's address and administrator, with the password handed out at delivery or at the owner's last
 * reset; null once the portal deleted it.
 */
type TenantAccess = { webUrl: string; adminUser: string; adminPassword: string | null; adminPasswordExpiresAt: string | null }
type SubdomainChangeResult = { subdomain: string; status: 'UNCHANGED' | 'UPDATED' | 'QUEUED' }
/** The administrator released and the device that held them. */
type AdministratorRelease = { adminUser: string; client: 'mobile' | 'desktop' | null; since: string; lastContact: string }
const originLabels: Record<Origin, string> = { KNOWN_EXISTING: 'Cliente registrado', UNVERIFIED_EXISTING: 'Cliente existente por validar', NEW_PURCHASE: 'Cliente nuevo' }

export function CompaniesPage() {
  const [companies, setCompanies] = useState<Company[]>([])
  const [businessName, setBusinessName] = useState('')
  const [error, setError] = useState('')
  const [loadError, setLoadError] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [access, setAccess] = useState<Record<string, TenantAccess>>({})
  const [accessLoading, setAccessLoading] = useState('')
  const [accessError, setAccessError] = useState<Record<string, string>>({})
  const [subdomainEditing, setSubdomainEditing] = useState('')
  const [subdomainDraft, setSubdomainDraft] = useState('')
  const [subdomainSaving, setSubdomainSaving] = useState(false)
  const [subdomainNotice, setSubdomainNotice] = useState<Record<string, string>>({})
  const [lineOfBusiness, setLineOfBusiness] = useState<LineOfBusiness | ''>('')
  const [lineEditing, setLineEditing] = useState('')
  const [lineDraft, setLineDraft] = useState<LineOfBusiness>('RETAIL')
  const [lineSaving, setLineSaving] = useState(false)
  const [lineNotice, setLineNotice] = useState<Record<string, string>>({})
  const idempotencyKey = useRef(crypto.randomUUID())
  const formRef = useRef<HTMLFormElement>(null)
  const [trialIntent, setTrialIntent] = useState<Company | null>(null)
  const [trialBusy, setTrialBusy] = useState(false)
  const [trialError, setTrialError] = useState('')
  const [trialTermsAccepted, setTrialTermsAccepted] = useState(false)
  const [trialNotice, setTrialNotice] = useState<Record<string, string>>({})
  const trialKey = useRef(crypto.randomUUID())
  const [deleteIntent, setDeleteIntent] = useState<Company | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [deleteError, setDeleteError] = useState('')
  const [deleted, setDeleted] = useState('')
  const [resetIntent, setResetIntent] = useState<Company | null>(null)
  const [resetPassword, setResetPassword] = useState('')
  const [resetBusy, setResetBusy] = useState(false)
  const [resetError, setResetError] = useState('')
  const [resetNotice, setResetNotice] = useState<Record<string, string>>({})
  const [releaseIntent, setReleaseIntent] = useState<Company | null>(null)
  const [releasePassword, setReleasePassword] = useState('')
  const [releaseBusy, setReleaseBusy] = useState(false)
  const [releaseError, setReleaseError] = useState('')
  const [releaseNotice, setReleaseNotice] = useState<Record<string, string>>({})

  const load = useCallback(async () => {
    setLoadError('')
    setLoading(true)
    try {
      setCompanies(await api.get<Company[]>('/companies'))
    } catch (cause) {
      setLoadError(cause instanceof ApiError ? cause.message : 'No pudimos cargar tus empresas.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError('')
    if (!businessName.trim()) {
      setError('Escribe el nombre del negocio.')
      formRef.current?.querySelector<HTMLInputElement>('input')?.focus()
      return
    }
    if (!lineOfBusiness) {
      setError('Elige el giro del negocio.')
      formRef.current?.querySelector<HTMLSelectElement>('#company-line-of-business')?.focus()
      return
    }
    setSaving(true)
    try {
      // The portal registers new customers only; every business runs in the cloud, so no hosting is sent.
      await api.post<Company>('/companies', { businessName: businessName.trim(), lineOfBusiness }, { 'Idempotency-Key': idempotencyKey.current })
      setBusinessName('')
      setLineOfBusiness('')
      idempotencyKey.current = crypto.randomUUID()
      await load()
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'No pudimos crear la empresa.')
    } finally {
      setSaving(false)
    }
  }

  async function revealAccess(company: Company) {
    setAccessLoading(company.id)
    setAccessError((current) => ({ ...current, [company.id]: '' }))
    try {
      const result = await api.get<TenantAccess>(`/companies/${company.id}/tenant-access`)
      setAccess((current) => ({ ...current, [company.id]: result }))
    } catch (cause) {
      setAccessError((current) => ({ ...current, [company.id]: cause instanceof ApiError ? cause.message : 'No pudimos recuperar el acceso.' }))
    } finally {
      setAccessLoading('')
    }
  }

  async function resetAdministrator() {
    if (!resetIntent) return
    const company = resetIntent
    setResetBusy(true); setResetError('')
    try {
      const result = await api.post<TenantAccess>(`/companies/${company.id}/tenant-access/admin-password`, { password: resetPassword })
      setAccess((current) => ({ ...current, [company.id]: result }))
      setAccessError((current) => ({ ...current, [company.id]: '' }))
      setResetNotice((current) => ({ ...current, [company.id]: `${result.adminUser} tiene una contraseña nueva y sus sesiones abiertas se cerraron, salvo la de un equipo que lo tenga retenido. Te lo avisamos también por correo.` }))
      setResetIntent(null)
      setResetPassword('')
    } catch (cause) {
      setResetError(cause instanceof ApiError ? cause.message : 'No pudimos restablecer la contraseña. Intenta de nuevo.')
    } finally {
      setResetBusy(false)
    }
  }

  async function releaseAdministrator() {
    if (!releaseIntent) return
    const company = releaseIntent
    setReleaseBusy(true); setReleaseError('')
    try {
      const result = await api.post<AdministratorRelease>(`/companies/${company.id}/tenant-access/admin-release`, { password: releasePassword })
      setReleaseNotice((current) => ({ ...current, [company.id]: `${result.adminUser} quedó liberado: ya puede iniciar sesión en otro equipo. Te lo avisamos también por correo.` }))
      setReleaseIntent(null)
      setReleasePassword('')
    } catch (cause) {
      setReleaseError(cause instanceof ApiError ? cause.message : 'No pudimos liberar al administrador. Intenta de nuevo.')
    } finally {
      setReleaseBusy(false)
    }
  }

  async function saveSubdomain(event: FormEvent, company: Company) {
    event.preventDefault()
    setSubdomainSaving(true)
    setSubdomainNotice((current) => ({ ...current, [company.id]: '' }))
    try {
      const result = await api.patch<SubdomainChangeResult>(`/companies/${company.id}/subdomain`, { subdomain: subdomainDraft })
      setSubdomainNotice((current) => ({ ...current, [company.id]: result.status === 'QUEUED' ? 'El nuevo subdominio se activará en unos momentos.' : 'El subdominio quedó actualizado.' }))
      setSubdomainEditing('')
      await load()
    } catch (cause) {
      setSubdomainNotice((current) => ({ ...current, [company.id]: cause instanceof ApiError ? cause.message : 'No pudimos cambiar el subdominio.' }))
    } finally {
      setSubdomainSaving(false)
    }
  }

  async function startTrial() {
    if (!trialIntent) return
    setTrialBusy(true); setTrialError('')
    try {
      await api.post(`/software/trial`, { companyId: trialIntent.id, environmentId: trialIntent.environmentId, termsVersion }, { 'Idempotency-Key': trialKey.current })
      setTrialNotice((current) => ({ ...current, [trialIntent.id]: 'Tu prueba gratis empezó: estamos preparando el entorno. En unos segundos verás aquí su dirección y tu primer usuario.' }))
      setTrialIntent(null)
      trialKey.current = crypto.randomUUID()
      await load()
    } catch (cause) {
      setTrialError(cause instanceof ApiError ? cause.message : 'No pudimos iniciar la prueba. Intenta de nuevo.')
    } finally {
      setTrialBusy(false)
    }
  }

  async function deleteCompany() {
    if (!deleteIntent) return
    setDeleteBusy(true); setDeleteError('')
    try {
      await api.delete(`/companies/${deleteIntent.id}`)
      setDeleted(`Eliminamos ${deleteIntent.businessName}.`)
      setDeleteIntent(null)
      await load()
    } catch (cause) {
      setDeleteError(cause instanceof ApiError ? cause.message : 'No pudimos eliminar la empresa. Intenta de nuevo.')
    } finally {
      setDeleteBusy(false)
    }
  }

  async function saveLineOfBusiness(event: FormEvent, company: Company) {
    event.preventDefault()
    setLineSaving(true)
    setLineNotice((current) => ({ ...current, [company.id]: '' }))
    try {
      await api.patch<Company>(`/companies/${company.id}/line-of-business`, { lineOfBusiness: lineDraft })
      setLineNotice((current) => ({ ...current, [company.id]: 'El giro quedó actualizado.' }))
      setLineEditing('')
      await load()
    } catch (cause) {
      setLineNotice((current) => ({ ...current, [company.id]: cause instanceof ApiError ? cause.message : 'No pudimos cambiar el giro.' }))
    } finally {
      setLineSaving(false)
    }
  }

  return <section className="portal-page companies-page">
    <header className="flow-heading"><p className="eyebrow">Mi cuenta</p><h1>Mis empresas</h1><p>Cada empresa mantiene sus licencias, equipos y responsables separados.</p></header>
    {loading && companies.length === 0 ? <LoadingState label="Cargando tus empresas…" /> : loadError && companies.length === 0 ? <ErrorState title={loadError} retry={() => void load()} /> : <>
      {loadError && <Notice>{loadError}</Notice>}
      {error && <Notice>{error}</Notice>}
      {deleted && <Notice tone="info">{deleted}</Notice>}
      <div className="companies-grid">
        <div className="companies-list">
          {companies.length === 0 ? <div className="address-empty"><h2>Aún no tienes empresas</h2><p>Registra la primera para administrar licencias.</p></div> : companies.map((company) => <article key={company.id} className="company-card">
            <div className="company-summary"><h2>{company.businessName}</h2><p>{originLabels[company.origin]}{company.lineOfBusiness ? ` · ${lineOfBusinessLabels[company.lineOfBusiness]}` : ''} · {company.role === 'OWNER' ? 'Propietario' : company.role}</p>
              <div className="tenant-delivery" data-status={company.provisioningStatus}>
                <strong>{deliveryLabels[company.provisioningStatus] ?? company.provisioningStatus}</strong>
                {company.webUrl && <a href={company.webUrl} target="_blank" rel="noreferrer">{company.webUrl}</a>}
                {company.role === 'OWNER' && company.provisioningStatus === 'AWAITING_PAYMENT' && company.trialAvailable && <Button emphasis="solid" onClick={() => { setTrialError(''); setTrialTermsAccepted(false); setTrialIntent(company) }}>Probar gratis 7 días</Button>}
                {trialNotice[company.id] && <Notice tone="info">{trialNotice[company.id]}</Notice>}
                {company.role === 'OWNER' && company.provisioningStatus !== 'PREPARING' && subdomainEditing !== company.id && <Button emphasis="ghost" onClick={() => { setSubdomainEditing(company.id); setSubdomainDraft(company.subdomain ?? '') }}>Cambiar subdominio</Button>}
                {subdomainEditing === company.id && <form className="subdomain-form" onSubmit={(event) => void saveSubdomain(event, company)}>
                  <label htmlFor={`subdomain-${company.id}`}>Subdominio</label>
                  <div><input id={`subdomain-${company.id}`} value={subdomainDraft} onChange={(event) => setSubdomainDraft(event.target.value.toLowerCase())} maxLength={63} pattern="[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?" required autoFocus /><Button type="submit" busy={subdomainSaving}>Guardar</Button><Button emphasis="ghost" onClick={() => setSubdomainEditing('')}>Cancelar</Button></div>
                </form>}
                {subdomainNotice[company.id] && <Notice tone={subdomainNotice[company.id]?.startsWith('No ') ? undefined : 'info'}>{subdomainNotice[company.id]}</Notice>}
                {company.role === 'OWNER' && company.provisioningStatus === 'AWAITING_PAYMENT' && lineEditing !== company.id && <Button emphasis="ghost" onClick={() => { setLineEditing(company.id); setLineDraft(company.lineOfBusiness ?? 'RETAIL') }}>Cambiar giro</Button>}
                {lineEditing === company.id && <form className="line-of-business-form" noValidate onSubmit={(event) => void saveLineOfBusiness(event, company)}>
                  <NativeSelect label="Giro" id={`line-of-business-${company.id}`} value={lineDraft} onChange={(event) => setLineDraft(event.target.value as LineOfBusiness)} autoFocus>{lineOfBusinessOrder.map((value) => <option key={value} value={value}>{lineOfBusinessLabels[value]}</option>)}</NativeSelect>
                  <p className="field-hint">{lineOfBusinessHint}</p>
                  <div className="line-of-business-actions"><Button type="submit" busy={lineSaving}>Guardar giro</Button><Button emphasis="ghost" onClick={() => setLineEditing('')}>Cancelar</Button></div>
                </form>}
                {lineNotice[company.id] && <Notice tone={lineNotice[company.id]?.startsWith('El giro quedó') ? 'info' : undefined}>{lineNotice[company.id]}</Notice>}
                {company.provisioningStatus === 'READY' && company.role === 'OWNER' && !access[company.id] && <Button emphasis="outline" busy={accessLoading === company.id} onClick={() => void revealAccess(company)}>Mostrar acceso</Button>}
                {accessError[company.id] && <Notice>{accessError[company.id]}</Notice>}
                {access[company.id] && <dl className="tenant-credentials"><div><dt>Usuario</dt><dd>{access[company.id]?.adminUser}</dd></div>{access[company.id]?.adminPassword && <div><dt>Contraseña</dt><dd>{access[company.id]?.adminPassword}</dd></div>}</dl>}
                {access[company.id] && <p className="field-hint">{passwordHint(access[company.id]!)}</p>}
                {company.provisioningStatus === 'READY' && company.role === 'OWNER' && <div className="admin-operations" role="group" aria-labelledby={`admin-operations-${company.id}`}>
                  <strong id={`admin-operations-${company.id}`}>Operaciones del administrador</strong>
                  <div className="admin-operations-actions">
                    <Button emphasis="outline" onClick={() => { setResetError(''); setResetPassword(''); setResetIntent(company) }}>Restablecer contraseña</Button>
                    <Button emphasis="outline" onClick={() => { setReleaseError(''); setReleasePassword(''); setReleaseIntent(company) }}>Liberar</Button>
                  </div>
                  {resetNotice[company.id] && <Notice tone="info">{resetNotice[company.id]}</Notice>}
                  {releaseNotice[company.id] && <Notice tone="info">{releaseNotice[company.id]}</Notice>}
                </div>}
              </div>
            </div>
            <div className="company-actions">
              <Link className="ui-button" data-intent="primary" data-emphasis="solid" to={`/empresas/${company.id}/licencias`}>Administrar licencias</Link>
              {deletable(company) && <Button intent="danger" emphasis="ghost" onClick={() => { setDeleteError(''); setDeleted(''); setDeleteIntent(company) }}>Eliminar empresa</Button>}
            </div>
          </article>)}
        </div>
        <form ref={formRef} noValidate onSubmit={submit} className="portal-form company-form"><h2>Registrar una empresa</h2>
          {companies.length > 0 && <p className="field-hint">¿Tienes otro negocio? Regístralo aquí con esta misma cuenta: tendrá su propio entorno, dirección y licencias, separados de los demás.</p>}
          <div className="form-field"><label htmlFor="company-name">Nombre del negocio</label><input id="company-name" value={businessName} onChange={(event) => setBusinessName(event.target.value)} maxLength={200} required /></div>
          <div className="form-field"><NativeSelect label="Giro del negocio" id="company-line-of-business" value={lineOfBusiness} onChange={(event) => setLineOfBusiness(event.target.value as LineOfBusiness | '')} aria-describedby="company-line-of-business-hint"><option value="">Elige el giro</option>{lineOfBusinessOrder.map((value) => <option key={value} value={value}>{lineOfBusinessLabels[value]}</option>)}</NativeSelect><p id="company-line-of-business-hint" className="field-hint">{lineOfBusinessHint}</p></div>
          <Button type="submit" busy={saving}>Guardar empresa</Button>
        </form>
      </div>
    </>}
    <ConfirmDialog open={trialIntent !== null} title="Probar BsPOS gratis 7 días" action="Empezar la prueba" intent="primary" busy={trialBusy} disabled={!trialTermsAccepted} error={trialError || undefined} onCancel={() => { if (!trialBusy) setTrialIntent(null) }} onConfirm={() => void startTrial()}>
      <p>Preparamos el entorno de <strong>{trialIntent?.businessName}</strong> con lo que incluye el plan mensual: una sucursal con sus usuarios, la web, el escritorio y BsPOS móvil sin límite de equipos. Sin módulos, sin timbres y sin tarjeta.</p>
      <p>La prueba termina sola a los 7 días, sin cobro. Lo que registres se conserva 90 días después y vuelve si contratas un plan en ese plazo. Cada cuenta tiene una prueba.</p>
      <SupportLine />
      <TermsConsent checked={trialTermsAccepted} disabled={trialBusy} onChange={setTrialTermsAccepted} />
    </ConfirmDialog>
    <ConfirmDialog open={deleteIntent !== null} title="Eliminar empresa" action="Eliminar empresa" busy={deleteBusy} error={deleteError || undefined} onCancel={() => { if (!deleteBusy) setDeleteIntent(null) }} onConfirm={() => void deleteCompany()}>
      <p>Se borra <strong>{deleteIntent?.businessName}</strong> con su entorno{deleteIntent?.subdomain ? `, y el subdominio ${deleteIntent.subdomain} queda libre` : ''}. Quienes agregaste como miembros dejan de verla. No se puede deshacer.</p>
      <p>Solo se elimina una empresa que no ha tenido órdenes, prueba, pagos ni equipos. Si tiene alguno, te lo diremos aquí y lo atiende soporte.</p>
    </ConfirmDialog>
    <ConfirmDialog open={resetIntent !== null} title="Restablecer contraseña del administrador" action="Restablecer contraseña" intent="primary" busy={resetBusy} disabled={!resetPassword} error={resetError || undefined} onCancel={() => { if (!resetBusy) { setResetIntent(null); setResetPassword('') } }} onConfirm={() => void resetAdministrator()}>
      <p>El administrador de <strong>{resetIntent?.businessName}</strong> recibe una contraseña nueva, que verás aquí. Sirve para entrar una vez: al hacerlo, BsPOS pide elegir otra. Sus sesiones abiertas se cierran; los demás usuarios y los datos no cambian.</p>
      <p>Si un teléfono o una computadora lo tiene retenido, sigue retenido: restablecer la contraseña no lo libera. Para eso está «Liberar», en Operaciones del administrador.</p>
      <p>Úsalo cuando nadie de la tienda puede entrar como administrador. Si alguno puede, restablece la contraseña en BsPOS, en la pantalla de usuarios.</p>
      <PasswordField label="Tu contraseña del portal" autoComplete="current-password" value={resetPassword} onChange={setResetPassword} minLength={1} hint="La pedimos para confirmar que eres tú." />
    </ConfirmDialog>
    <ConfirmDialog open={releaseIntent !== null} title="Liberar al administrador" action="Liberar" busy={releaseBusy} disabled={!releasePassword} error={releaseError || undefined} onCancel={() => { if (!releaseBusy) { setReleaseIntent(null); setReleasePassword('') } }} onConfirm={() => void releaseAdministrator()}>
      <p>{(releaseIntent && access[releaseIntent.id]?.adminUser) ?? 'El administrador'} de <strong>{releaseIntent?.businessName}</strong> queda libre para entrar en otro equipo. El teléfono o la computadora que lo retiene deja de funcionar con esa sesión al instante.</p>
      <p><strong>Lo que ese equipo vendió o capturó sin conexión y todavía no envió se rechaza para siempre:</strong> ventas, pedidos, cobros y lo demás nunca llegarán al servidor, aunque alguien vuelva a entrar en ese equipo.</p>
      <p>Úsalo cuando el administrador quedó retenido en un equipo que ya no tienes o que nadie puede usar. Si otro administrador puede entrar, libéralo en BsPOS, en la pantalla de usuarios.</p>
      <PasswordField label="Tu contraseña del portal" autoComplete="current-password" value={releasePassword} onChange={setReleasePassword} minLength={1} hint="La pedimos para confirmar que eres tú." />
    </ConfirmDialog>
  </section>
}

/** What the server may still refuse; a company in use is never offered the deletion. */
function deletable(company: Company): boolean {
  if (company.role !== 'OWNER' || company.webUrl) return false
  return company.provisioningStatus === 'AWAITING_PAYMENT'
}

/** What the owner is told of the handed-out password: it serves for one sign-in, and until when it is kept here. */
function passwordHint(access: TenantAccess): string {
  if (!access.adminPassword) return 'Esa contraseña solo servía para un acceso y ya no la guardamos. Si nadie puede entrar como administrador, restablécela.'
  const until = access.adminPasswordExpiresAt ? ` La borramos de aquí el ${formatDate(access.adminPasswordExpiresAt)}.` : ''
  return `Sirve para un solo acceso: al entrar te pediremos cambiarla.${until}`
}
